import { generateDynamicPath } from 'common/utils/routing';

/**
 * Canonical home of the creator/admin surfaces. `/natalie/*` means the participant
 * experience; everything an admin sees lives under `/natalie-admin`.
 *
 * `/natalie-admin` is a distinct prefix from `/natalie`, not a sub-path of it: a
 * non-exact react-router match on `/natalie` requires a `/` or end-of-path boundary,
 * so `/natalie-admin…` never matches it. That is why the admin prefix needs its own
 * community gate (it is not covered by the `/natalie` one) and why the telemetry
 * `/natalie/:id` catch-all cannot read an admin path segment as an event id.
 */
export const NatalieAdminRoutes = {
  NatalieAdmin: '/natalie-admin',
  List: '/natalie-admin/list',
  EventDetails: '/natalie-admin/:id/details',
  // Entry point for the create_event intake flow, and the URL the CS team shares with a
  // customer contact. It starts nothing: it presents the two intake modes and lets the
  // requester pick. None of the three carries an event id on purpose — the intake event
  // does not exist until the requester finishes, so an id-bearing path could never be
  // shared in advance, and each mode is shareable on its own URL.
  CreateIntake: '/natalie-admin/create',
  CreateIntakeVoice: '/natalie-admin/create/voice',
  CreateIntakeForm: '/natalie-admin/create/form',
  // The self-serve creation flow, where a community admin describes the conversation they want
  // and comes away with a Natalie they have reviewed themselves. Two steps, two routes, each
  // reachable by direct link. It leaves the intake paths above untouched: communities on those
  // keep working while this is unfinished.
  //
  // `CreateSetup` joins the id-less intake modes, for the reason they give — nothing exists
  // until the organiser submits, so there is no id to put in the path. `Review` takes the
  // id-bearing admin shape `EventDetails` already uses, which puts it beside
  // `/natalie-admin/:id/details` and makes it the first step a returning link can reach.
  //
  // Neither collides with the intake paths. `create/setup` is a literal third segment that no
  // id-bearing pattern matches, because every one of those ends in a literal of its own
  // (`details`, `review`) — so `/natalie-admin/create/setup` can never be read as an event
  // called "create". The collision that IS live runs the other way: `CreateIntake` is matched
  // non-exactly, so `/natalie-admin/create/setup` falls inside it, which is why the Dashboard
  // registers this flow AHEAD of the intake block.
  CreateSetup: '/natalie-admin/create/setup',
  /**
   * Setup again, for a draft that already exists.
   *
   * The id-less `CreateSetup` above still starts the flow, and still cannot carry an id — nothing
   * exists until the organiser generates. This is the other door: once a draft does exist, the
   * organiser can be sent back to the answers it was generated from, and a path with no id in it
   * has no way to say which draft that is. So the two coexist rather than one replacing the other.
   *
   * It takes the id-bearing shape `Review` and `Summary` use, which keeps the flow's three steps
   * one family of URLs. `setup` is a literal last segment, so it collides with nothing: the
   * id-bearing patterns each end in a literal of their own.
   */
  Setup: '/natalie-admin/:id/setup',
  Review: '/natalie-admin/:id/review',
  /**
   * The flow's third step, which the step indicator calls Details.
   *
   * `summary` rather than `details`, because `/natalie-admin/:id/details` is the legacy admin
   * metrics page and is still reached from the list row's "View engagement". The URL and the label
   * differ on purpose and only until that page retires, at which point this can take the word
   * back.
   */
  Summary: '/natalie-admin/:id/summary',
};

export const NatalieRoutes = {
  // Every entry is a live participant surface, except bare `Natalie`, which redirects to
  // the canonical admin prefix. Nothing here is an admin path: the creator/admin surfaces
  // all live in `NatalieAdminRoutes` above.
  Natalie: '/natalie',
  BeforeJoin: '/natalie/:id/before-join',
  Details: '/natalie/:id',
  Explore: '/natalie/:id/explore',
  Feedback: '/natalie/:id/feedback',
  Join: '/natalie/:id/join',
  Public: '/natalie/:id/public',
  // Registered Room stays codeless in the path; a test session carries its code
  // as the `?testInviteCode=` query param, which every Natalie navigation
  // preserves. The Room's TEST indicator (pill) is driven by the server-resolved
  // `TokenResponse.isTest` at token mint, not by the URL, so a refresh / shared
  // Room URL still resolves test correctly.
  RoomLiveKit: '/natalie/:id/room/livekit',
  // Sign-in handoff for the standalone Natalie app (served same-origin under
  // /natalie-next). It renders no UI: it exists so an unauthenticated or
  // unregistered visitor to that app can be sent HERE, pick up this app's login
  // and profile gates for free, and be handed straight back. Adding it to this
  // table also reserves the `next` segment via RESERVED_NATALIE_SUBROUTES below,
  // so it can never be read as a guest invite code.
  NextHandoff: '/natalie/:id/next',
  WaitingRoom: '/natalie/:id/waiting-room',
  // Accountless (Guest) flow — the real invite code rides the path; a test
  // session adds the test code as `?testInviteCode=` (preserved across steps).
  AccountlessLanding: '/natalie/:eventId/:inviteCode',
  AccountlessJoin: '/natalie/:eventId/:inviteCode/join',
  AccountlessRoom: '/natalie/:eventId/:inviteCode/room',
  AccountlessFeedback: '/natalie/:eventId/:inviteCode/feedback',
  AccountlessExplore: '/natalie/:eventId/:inviteCode/explore',
};

// Static sub-route segments that share the `/natalie/:id/<segment>` shape with
// the accountless `/natalie/:eventId/:inviteCode` route. Any of these appearing
// in the `:inviteCode` position is a registered sub-route, NOT a guest invite
// code (e.g. "waiting-room" is 12 chars and matches the invite-code heuristic in
// PreApp). Derived from NatalieRoutes so a newly added sub-route can never
// silently re-introduce the collision.
export const RESERVED_NATALIE_SUBROUTES: ReadonlySet<string> = new Set(
  Object.values(NatalieRoutes)
    .map((path) => path.split('/')[3]) // the segment after `/natalie/:id`
    .filter((segment): segment is string => Boolean(segment) && !segment.startsWith(':'))
);

export const NataliePublicRoutes = [NatalieRoutes.Public, NatalieRoutes.Details];

// Routes where Intercom widget should be hidden (natalie module)
export const INTERCOM_HIDDEN_ROUTES: readonly string[] = [
  // Live Natalie routes
  NatalieRoutes.BeforeJoin,
  NatalieRoutes.Join,
  NatalieRoutes.WaitingRoom,
  // Public routes
  ...NataliePublicRoutes,
  // Accountless routes
  NatalieRoutes.AccountlessLanding,
  NatalieRoutes.AccountlessJoin,
  NatalieRoutes.AccountlessRoom,
  NatalieRoutes.AccountlessFeedback,
  NatalieRoutes.AccountlessExplore,
  NatalieRoutes.Explore,
];

// Routes where Intercom should be shown to anonymous (unauthenticated) users (natalie module)
export const INTERCOM_PUBLIC_ROUTES: readonly string[] = [NatalieRoutes.Public];

export const getNatalieDetailsPath = (id: string) => generateDynamicPath(NatalieRoutes.Details, { id });

export const getNatalieAdminEventDetailsPath = (id: string) =>
  generateDynamicPath(NatalieAdminRoutes.EventDetails, { id });

export const getNatalieAdminSetupPath = (id: string) => generateDynamicPath(NatalieAdminRoutes.Setup, { id });

export const getNatalieAdminReviewPath = (id: string) => generateDynamicPath(NatalieAdminRoutes.Review, { id });

export const getNatalieAdminSummaryPath = (id: string) => generateDynamicPath(NatalieAdminRoutes.Summary, { id });

export const getNataliePublicPath = (id: string) =>
  generateDynamicPath(NatalieRoutes.Public, { id }, undefined, { isAbsolute: true });

export const getNatalieFeedbackPath = (id: string) => generateDynamicPath(NatalieRoutes.Feedback, { id });

export const getNatalieExplorePath = (id: string) => generateDynamicPath(NatalieRoutes.Explore, { id });

export const getAccountlessLandingPath = (eventId: string, inviteCode: string) =>
  generateDynamicPath(NatalieRoutes.AccountlessLanding, { eventId, inviteCode });

export const getAccountlessLandingUrl = (eventId: string, inviteCode: string) =>
  generateDynamicPath(NatalieRoutes.AccountlessLanding, { eventId, inviteCode }, undefined, { isAbsolute: true });

export const getAccountlessJoinPath = (eventId: string, inviteCode: string) =>
  generateDynamicPath(NatalieRoutes.AccountlessJoin, { eventId, inviteCode });

export const getAccountlessRoomPath = (eventId: string, inviteCode: string) =>
  generateDynamicPath(NatalieRoutes.AccountlessRoom, { eventId, inviteCode });

export const getAccountlessFeedbackPath = (eventId: string, inviteCode: string) =>
  generateDynamicPath(NatalieRoutes.AccountlessFeedback, { eventId, inviteCode });

export const getAccountlessExplorePath = (eventId: string, inviteCode: string) =>
  generateDynamicPath(NatalieRoutes.AccountlessExplore, { eventId, inviteCode });
